/**
 * Gesture Analysis Utilities
 * Centralized functions for detecting alerts and warnings in gesture data
 */

// ============================================================================
// Habituality / analysis-method helpers (engine schema >= 1.4)
//
// New per-row fields emitted by the Python engine on alerts[] and episodes[]:
//   analysis_method: 'absolute' | 'baseline'
//   analysis_status: 'alert' | 'warning' | 'discarded'  (warning: gestures 5.0.0)
//   discard_reason:  null | 'sustained_not_valid' | 'habitual' | 'at_baseline'
//                    | 'suppressed_subject_speech' | 'too_short' (gestures 4.1.0)
//   frequency_band:  null | 'rare' | 'intermediate' | 'habitual'  (absolute only)
//   absolute_count:  null | int  (absolute only)
// Old analyses (schema 1.3) do not carry these keys: treat missing as
// analysis_method='baseline', analysis_status='alert'.
// ============================================================================

/** English labels for discard reasons (rows excluded from the analysis). */
export const DISCARD_REASON_LABELS = {
  sustained_not_valid: 'Sustained — not valid',
  habitual: 'Habitual',
  at_baseline: 'At baseline',
  suppressed_subject_speech: 'Suppressed while speaking',
  too_short: 'Too short (under 200 ms)',
};

/**
 * Baseline-method reasons. Includes both the backend long form
 * (above_baseline, rare_appearance, ...) and the short form used by
 * the timeline normalizer (above, below, rare, at_baseline).
 */
const BASELINE_REASON_LABELS = {
  above_baseline: 'above baseline',
  below_baseline: 'below baseline',
  rare_appearance: 'rare appearance',
  local_disappearance: 'local disappearance',
  at_baseline: 'at baseline',
  above: 'above baseline',
  below: 'below baseline',
  rare: 'rare appearance',
};

/** Absolute-method frequency bands (namespaced apart from baseline reasons). */
const FREQUENCY_BAND_LABELS = {
  rare: 'rare',
  intermediate: 'intermediate',
  habitual: 'habitual',
};

/**
 * Normalize the new analysis-method fields from an alert/episode row.
 * Missing keys (old schema 1.3) default to baseline/alert.
 * @param {Object} row - Raw alert or episode row from gesture_results.
 * @returns {{analysis_method: string, analysis_status: string, discard_reason: string|null,
 *            frequency_band: string|null, absolute_count: number|null, has_method_data: boolean}}
 */
export function normalizeAnalysisMethodFields(row) {
  const src = row && typeof row === 'object' ? row : {};
  const hasMethodData =
    src.analysis_method !== undefined ||
    src.analysisMethod !== undefined ||
    src.analysis_status !== undefined ||
    src.analysisStatus !== undefined;
  const methodRaw = String(src.analysis_method ?? src.analysisMethod ?? 'baseline').toLowerCase();
  const method = methodRaw === 'absolute' ? 'absolute' : 'baseline';
  const statusRaw = String(src.analysis_status ?? src.analysisStatus ?? 'alert').toLowerCase();
  // 'warning' (gestures 5.0.0): the intermediate band, shown but never subtracting.
  const status = statusRaw === 'discarded' || statusRaw === 'warning' ? statusRaw : 'alert';
  const discardReason = src.discard_reason ?? src.discardReason ?? null;
  let band = src.frequency_band ?? src.frequencyBand ?? null;
  // Absolute rows ship the band in `reason` too; use it as fallback.
  if (!band && method === 'absolute') {
    const reasonRaw = String(src.reason ?? '').toLowerCase().trim();
    if (FREQUENCY_BAND_LABELS[reasonRaw]) band = reasonRaw;
  }
  const countRaw = src.absolute_count ?? src.absoluteCount;
  const count = Number(countRaw);
  return {
    analysis_method: method,
    analysis_status: status,
    discard_reason: discardReason || null,
    frequency_band: band || null,
    absolute_count: countRaw != null && Number.isFinite(count) ? count : null,
    has_method_data: hasMethodData,
  };
}

/**
 * Compact chip label for the habituality method of a row.
 * Examples: 'Absolute · rare', 'Baseline · above baseline'.
 * Band names (absolute) and baseline reasons are namespaced by the method
 * prefix so 'rare' (absolute band) is never confused with 'rare appearance'
 * (baseline reason).
 * @param {Object} row - Alert row (raw or normalized) with the method fields.
 * @returns {string|null}
 */
export function methodChipLabel(row) {
  if (!row || typeof row !== 'object') return null;
  const info = normalizeAnalysisMethodFields(row);
  if (info.analysis_method === 'absolute') {
    const band = String(info.frequency_band || '').toLowerCase().trim();
    const bandLabel = FREQUENCY_BAND_LABELS[band] || null;
    return bandLabel ? `Absolute · ${bandLabel}` : 'Absolute';
  }
  const reasonRaw = String(row.reason ?? row.explain?.reason ?? '').toLowerCase().trim();
  const reasonLabel = BASELINE_REASON_LABELS[reasonRaw] || null;
  return reasonLabel ? `Baseline · ${reasonLabel}` : 'Baseline';
}

/**
 * English label for a discard reason value.
 * @param {string|null} reason
 * @returns {string}
 */
export function discardReasonLabel(reason) {
  const key = String(reason ?? '').toLowerCase().trim();
  return DISCARD_REASON_LABELS[key] || (key ? key.replace(/_/g, ' ') : 'Discarded');
}

/**
 * Build a lookup index from the raw gesture_results document so shaped rows
 * (which were stripped to a field whitelist in the store) can be re-joined
 * with the new analysis-method fields.
 *
 * Lookup keys: episode_id (primary), `${floor(timestampMs)}|${gesture_name}` (fallback).
 * @param {Object|null} gesturesDoc - Raw doc from analysisData/getGestures (episodes/alerts).
 * @returns {{byEpisodeId: Map, byTimeAndName: Map}}
 */
export function buildGestureMethodIndex(gesturesDoc) {
  const index = { byEpisodeId: new Map(), byTimeAndName: new Map() };
  if (!gesturesDoc || typeof gesturesDoc !== 'object') return index;

  const episodes = Array.isArray(gesturesDoc.episodes) ? gesturesDoc.episodes : [];
  const episodeStartById = new Map();
  episodes.forEach((ep) => {
    const id = ep?.episode_id ?? ep?.episodeId;
    const start = Number(ep?.start_time_ms ?? ep?.startTimeMs);
    if (id != null && Number.isFinite(start)) episodeStartById.set(String(id), start);
  });

  const register = (row) => {
    if (!row || typeof row !== 'object') return;
    const info = normalizeAnalysisMethodFields(row);
    const eid = row.episode_id ?? row.episodeId;
    if (eid != null && !index.byEpisodeId.has(String(eid))) {
      index.byEpisodeId.set(String(eid), info);
    }
    let ts = null;
    for (const key of ['timestamp_ms', 'timestampMs', 'start_time_ms', 'startTimeMs', 'time_ms', 'timeMs']) {
      if (row[key] == null) continue;
      const n = Number(row[key]);
      if (Number.isFinite(n)) {
        ts = n;
        break;
      }
    }
    if (ts === null && eid != null && episodeStartById.has(String(eid))) {
      ts = episodeStartById.get(String(eid));
    }
    const name = String(row.gesture_name ?? row.gestureName ?? '').trim();
    if (ts !== null && name) {
      const key = `${Math.floor(ts)}|${name}`;
      if (!index.byTimeAndName.has(key)) index.byTimeAndName.set(key, info);
    }
  };

  (Array.isArray(gesturesDoc.alerts) ? gesturesDoc.alerts : []).forEach(register);
  return index;
}

/**
 * Find the analysis-method info for a shaped row via the method index.
 * @param {{byEpisodeId: Map, byTimeAndName: Map}|null} index
 * @param {{episodeId?: string|null, timestampMs?: number|null, gestureName?: string|null}} ref
 * @returns {Object|null} - Normalized method fields or null when not found.
 */
export function lookupGestureMethodInfo(index, { episodeId = null, timestampMs = null, gestureName = null } = {}) {
  if (!index) return null;
  if (episodeId != null && index.byEpisodeId.has(String(episodeId))) {
    return index.byEpisodeId.get(String(episodeId));
  }
  const name = String(gestureName ?? '').trim();
  const ts = Number(timestampMs);
  if (name && Number.isFinite(ts)) {
    const key = `${Math.floor(ts)}|${name}`;
    if (index.byTimeAndName.has(key)) return index.byTimeAndName.get(key);
  }
  return null;
}

/**
 * Magnitude values for different alert types
 */
const ALERT_MAGNITUDES = {
  // Red alerts (alert level)
  brows_alert: 0.80,
  lips_alert: 0.80,
  nose_alert: 0.80,
  eye_wide_open: 0.80,
  mouth_twisted_left: 0.80,
  mouth_twisted_right: 0.80,
  nodding: 0.80,
  shaking: 0.80,
  light_nodding: 0.05,
  light_shaking: 0.05,
  shrug_detected: 0.60,
  index_below_nose_touched: 0.80,
  contempt_alert: 0.80,

  // Orange warnings (warning level)
  head_pose_tilted_right: 0.05,
  direction_up_right: 0.05,
  direction_right: 0.05,
  direction_left: 0.05,
  direction_up_left: 0.05,
  cranium_touched: 0.15,
  eye_touched: 0.05,
  ear_touched: 0.15,
  nose_touched: 0.15,
  pinching_nose_touched: 0.15,
  neck_touched: 0.15,
  hands_velocity_alert: 0.10,
  hands_touching_alert: 0.05,
};

/**
 * Infer landmark region/category from compact ai-service rows.
 * These rows usually expose `alert`/`gesture_name` and `reason`.
 */
function inferRegionFromNewFormat(gestureData) {
  // Hand classes are decided by their catalogue id before any token sniffing:
  // manipulation_touch_nose / _eye name a face region but are hand contacts,
  // and the facial branches below would otherwise claim them. Raw ai-service
  // alerts carry the id in `gesture_name`; table rows built by
  // analysisData.getGestureTableRows carry it in `alert`.
  const gestureName = String(gestureData?.gesture_name || gestureData?.alert || '')
    .trim()
    .toLowerCase();
  if (gestureName.startsWith('manipulation_') || gestureName.startsWith('illustrator_')) {
    return { region: 'hands', category: 'hands' };
  }

  const tokens = [
    gestureData?.alert,
    gestureData?.gesture_name,
    gestureData?.reason,
    gestureData?.brows,
    gestureData?.lips,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  // Facial regions
  if (tokens.includes('brow') || tokens.includes('ceja')) {
    return { region: 'brows', category: 'facial_expression' };
  }
  if (
    tokens.includes('lip') ||
    tokens.includes('labio') ||
    tokens.includes('mouth')
  ) {
    return { region: 'lips', category: 'facial_expression' };
  }
  if (tokens.includes('nose') || tokens.includes('nariz')) {
    return { region: 'nose', category: 'facial_expression' };
  }
  if (tokens.includes('eye') || tokens.includes('eyes') || tokens.includes('ojo')) {
    return { region: 'eyes', category: 'facial_expression' };
  }

  // Full head gestures
  if (
    tokens.includes('nodd') ||
    tokens.includes('shak') ||
    tokens.includes('head') ||
    tokens.includes('gaze') ||
    tokens.includes('direction') ||
    tokens.includes('shrug') ||
    tokens.includes('tilt')
  ) {
    return { region: 'full_head', category: 'head_gesture' };
  }

  // Hand/self-touch gestures
  if (
    tokens.includes('hand') ||
    tokens.includes('touch') ||
    tokens.includes('index') ||
    tokens.includes('finger') ||
    tokens.includes('pinch')
  ) {
    return { region: 'hands', category: 'hands' };
  }

  // Safe fallback: keep full head visual alert when unknown.
  return { region: 'full_head', category: 'head_gesture' };
}

/**
 * Simplified alert detection using backend-filtered data
 * Backend provides gesture_type ('gesture' or 'sustained') and should_show flag
 */
function detectAlertsFromBackend(gestureData) {
  const alerts = [];
  const warnings = [];
  let totalMagnitude = 0;

  // Process gesture_expressions
  for (const fieldName of ['brows', 'lips', 'nose']) {
    const gestureType = gestureData[`${fieldName}_gesture_type`];
    const shouldShow = gestureData[`${fieldName}_should_show`];
    
    if (gestureType && shouldShow) {
      const alertData = {
        type: fieldName,
        label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
        magnitude: gestureType === 'gesture' ? 0.8 : 0.15,
        category: 'facial_expression',
        region: fieldName,
      };
      
      if (gestureType === 'gesture') {
        alerts.push(alertData);
        totalMagnitude += alertData.magnitude;
      } else if (gestureType === 'sustained') {
        warnings.push(alertData);
        totalMagnitude += alertData.magnitude;
      }
    }
  }

  // Process boolean gestures
  for (const fieldName of ['nodding', 'shaking', 'light_nodding', 'light_shaking', 'shrug_detected']) {
    const gestureType = gestureData[`${fieldName}_gesture_type`];
    const shouldShow = gestureData[`${fieldName}_should_show`];
    
    if (gestureType && shouldShow) {
      const isGesture = gestureType === 'gesture';
      const magnitude = isGesture ? ALERT_MAGNITUDES[fieldName] || 0.8 : ALERT_MAGNITUDES[fieldName] || 0.05;
      
      const alertData = {
        type: fieldName,
        label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        magnitude,
        category: 'head_gesture',
        region: 'full_head',
      };
      
      if (isGesture) {
        alerts.push(alertData);
      } else {
        warnings.push(alertData);
      }
      totalMagnitude += magnitude;
    }
  }

  // Process touch gestures
  for (const fieldName of ['neck_touched', 'cranium_touched', 'eye_touched', 'ear_touched', 
                           'nose_touched', 'pinching_nose_touched', 'index_below_nose_touched']) {
    const gestureType = gestureData[`${fieldName}_gesture_type`];
    const shouldShow = gestureData[`${fieldName}_should_show`];
    
    if (gestureType && shouldShow) {
      const magnitude = ALERT_MAGNITUDES[fieldName] || 0.15;
      const alertData = {
        type: fieldName,
        label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        magnitude,
        category: 'self_touching',
      };
      
      if (gestureType === 'gesture') {
        alerts.push(alertData);
      } else {
        warnings.push(alertData);
      }
      totalMagnitude += magnitude;
    }
  }

  return { alerts, warnings, totalMagnitude };
}

/**
 * Calculate baseline statistics for gestures from all gesture data
 * @deprecated Baseline should be calculated in the backend, not in the frontend
 * @param {Array} allGestureData - Array of all gesture data objects
 * @returns {Object} - Always returns empty object (baseline must come from backend)
 */
export function calculateBaseline(allGestureData) {
  // CRITICAL: Baseline calculation should be done in the backend, not in the frontend
  // This function is deprecated and always returns an empty object
  // The backend should provide baseline statistics in the gesture data response
  console.warn('[calculateBaseline] DEPRECATED: Baseline should come from backend, not calculated in frontend');
  return {};
  
  // OLD CODE BELOW - DO NOT USE (kept for reference only)
  /*
  if (!Array.isArray(allGestureData) || allGestureData.length === 0) {
    return {};
  }

  // If we have very few gestures (< 10), be more lenient with baseline calculation
  const hasFewGestures = allGestureData.length < 10;

  const baseline = {
    // Gestures frequencies
    brows_alert: 0,
    lips_alert: 0,
    nose_alert: 0,
    eye_wide_open: 0,
    mouth_twisted: 0,
    contempt_alert: 0,
    // Head gestures
    nodding: 0,
    shaking: 0,
    light_nodding: 0,
    light_shaking: 0,
    shrug_detected: 0,
    head_pose_tilted_right: 0,
    direction: 0,
    // Self-touching
    index_below_nose_touched: 0,
    cranium_touched: 0,
    eye_touched: 0,
    ear_touched: 0,
    nose_touched: 0,
    pinching_nose_touched: 0,
    neck_touched: 0,
    // Hands
    hands_velocity_alert: 0,
    hands_touching_alert: 0,
  };

  let totalFrames = 0;

  // Count occurrences of each gesture across all data
  allGestureData.forEach((gesture) => {
    totalFrames++;

    // Brows
    if (
      (gesture.brows === 'brows_down' || gesture.brows === 'brows_up') &&
      gesture.brow_alert === true
    ) {
      baseline.brows_alert++;
    }

    // Lips
    if (
      gesture.is_speaking === false &&
      gesture.lip_alert === true &&
      (gesture.lips === 'lips_pressed' ||
        gesture.lips === 'lips_inverted' ||
        gesture.lips === 'lips_back')
    ) {
      baseline.lips_alert++;
    }

    // Nose
    if (gesture.nose === 'nose_wrinkled' && gesture.nose_alert === true) {
      baseline.nose_alert++;
    }

    // Eye status
    if (gesture.eye_status === 'wide_open') {
      baseline.eye_wide_open++;
    }

    // Mouth twisted
    if (gesture.mouth_twisted && gesture.mouth_twisted !== 'normal') {
      if (
        (gesture.mouth_twisted === 'left' && gesture.mouth_alert === true) ||
        gesture.mouth_twisted === 'right'
      ) {
        baseline.mouth_twisted++;
      }
    }

    // Contempt
    if (gesture.contempt_alert === true) {
      baseline.contempt_alert++;
    }

    // Head gestures
    if (gesture.nodding === true) {
      baseline.nodding++;
    }
    if (gesture.shaking === true) {
      baseline.shaking++;
    }
    if (gesture.light_nodding === true) {
      baseline.light_nodding++;
    }
    if (gesture.light_shaking === true) {
      baseline.light_shaking++;
    }
    if (gesture.shrug_detected === true) {
      baseline.shrug_detected++;
    }
    if (gesture.head_pose === 'tilted right' || gesture.head_pose === 'Tilted right') {
      baseline.head_pose_tilted_right++;
    }
    if (gesture.direction && 
        (gesture.direction === 'up_right' || 
         gesture.direction === 'right' || 
         gesture.direction === 'left' || 
         gesture.direction === 'up_left')) {
      baseline.direction++;
    }

    // Self-touching
    if (gesture.index_below_nose_touched === true) {
      baseline.index_below_nose_touched++;
    }
    if (gesture.cranium_touched === true) {
      baseline.cranium_touched++;
    }
    if (gesture.eye_touched === true) {
      baseline.eye_touched++;
    }
    if (gesture.ear_touched === true) {
      baseline.ear_touched++;
    }
    if (gesture.nose_touched === true) {
      baseline.nose_touched++;
    }
    if (gesture.pinching_nose_touched === true) {
      baseline.pinching_nose_touched++;
    }
    if (gesture.neck_touched === true) {
      baseline.neck_touched++;
    }

    // Hands
    if (gesture.hands_velocity_alert === true) {
      baseline.hands_velocity_alert++;
    }
    if (gesture.hands_touching_alert === true) {
      baseline.hands_touching_alert++;
    }
  });

  // Calculate frequencies per second (assuming 2 frames per second, 0.5s per frame)
  const totalSeconds = totalFrames * 0.5;
  
  // Convert to frequencies per second
  Object.keys(baseline).forEach((key) => {
    baseline[key] = totalSeconds > 0 ? baseline[key] / totalSeconds : 0;
  });

  // When we have few gestures, baseline might be unreliable - log for debugging
  if (hasFewGestures && Math.random() < 0.5) {
    console.log(`[calculateBaseline] Few gestures (${totalFrames}), baseline might be unreliable:`, {
      totalFrames,
      totalSeconds,
      sampleBaseline: Object.keys(baseline).slice(0, 10).reduce((acc, key) => {
        acc[key] = baseline[key].toFixed(4);
        return acc;
      }, {}),
    });
  }

  // Log baseline with more detail for debugging
  if (Math.random() < 0.1) { // Log 10% of the time to avoid spam
    console.log('Baseline calculated:', {
      totalFrames,
      totalSeconds,
      sampleBaseline: Object.keys(baseline).slice(0, 5).reduce((acc, key) => {
        acc[key] = baseline[key];
        return acc;
      }, {}),
    });
  }

  return baseline;
  */
}

/**
 * Calculate current frequency of gestures in a moving window
 * @param {Array} gestureDataWindow - Array of gesture data in the current window
 * @returns {Object} - Current frequencies per second
 */
function calculateCurrentFrequency(gestureDataWindow) {
  if (!Array.isArray(gestureDataWindow) || gestureDataWindow.length === 0) {
    return {};
  }

  const frequencies = {
    // Alerts (red)
    brows_alert: 0,
    lips_alert: 0,
    nose_alert: 0,
    eye_wide_open: 0,
    mouth_twisted: 0,
    contempt_alert: 0,
    nodding: 0,
    shaking: 0,
    shrug_detected: 0,
    index_below_nose_touched: 0,
    // Warnings (orange)
    light_nodding: 0,
    light_shaking: 0,
    head_pose_tilted_right: 0,
    direction: 0,
    cranium_touched: 0,
    eye_touched: 0,
    ear_touched: 0,
    nose_touched: 0,
    pinching_nose_touched: 0,
    neck_touched: 0,
    hands_velocity_alert: 0,
    hands_touching_alert: 0,
  };

  gestureDataWindow.forEach((gesture) => {
    // Brows
    if (
      (gesture.brows === 'brows_down' || gesture.brows === 'brows_up') &&
      gesture.brow_alert === true
    ) {
      frequencies.brows_alert++;
    }

    // Lips
    if (
      gesture.is_speaking === false &&
      gesture.lip_alert === true &&
      (gesture.lips === 'lips_pressed' ||
        gesture.lips === 'lips_inverted' ||
        gesture.lips === 'lips_back')
    ) {
      frequencies.lips_alert++;
    }

    // Nose
    if (gesture.nose === 'nose_wrinkled' && gesture.nose_alert === true) {
      frequencies.nose_alert++;
    }

    // Eye status
    if (gesture.eye_status === 'wide_open') {
      frequencies.eye_wide_open++;
    }

    // Mouth twisted
    if (gesture.mouth_twisted && gesture.mouth_twisted !== 'normal') {
      if (
        (gesture.mouth_twisted === 'left' && gesture.mouth_alert === true) ||
        gesture.mouth_twisted === 'right'
      ) {
        frequencies.mouth_twisted++;
      }
    }

    // Contempt
    if (gesture.contempt_alert === true) {
      frequencies.contempt_alert++;
    }

    // Head gestures
    if (gesture.nodding === true) {
      frequencies.nodding++;
    }
    if (gesture.shaking === true) {
      frequencies.shaking++;
    }
    if (gesture.light_nodding === true) {
      frequencies.light_nodding++;
    }
    if (gesture.light_shaking === true) {
      frequencies.light_shaking++;
    }
    if (gesture.shrug_detected === true) {
      frequencies.shrug_detected++;
    }
    if (gesture.head_pose === 'tilted right' || gesture.head_pose === 'Tilted right') {
      frequencies.head_pose_tilted_right++;
    }
    if (gesture.direction && 
        (gesture.direction === 'up_right' || 
         gesture.direction === 'right' || 
         gesture.direction === 'left' || 
         gesture.direction === 'up_left')) {
      frequencies.direction++;
    }

    // Self-touching
    if (gesture.index_below_nose_touched === true) {
      frequencies.index_below_nose_touched++;
    }
    if (gesture.cranium_touched === true) {
      frequencies.cranium_touched++;
    }
    if (gesture.eye_touched === true) {
      frequencies.eye_touched++;
    }
    if (gesture.ear_touched === true) {
      frequencies.ear_touched++;
    }
    if (gesture.nose_touched === true) {
      frequencies.nose_touched++;
    }
    if (gesture.pinching_nose_touched === true) {
      frequencies.pinching_nose_touched++;
    }
    if (gesture.neck_touched === true) {
      frequencies.neck_touched++;
    }

    // Hands
    if (gesture.hands_velocity_alert === true) {
      frequencies.hands_velocity_alert++;
    }
    if (gesture.hands_touching_alert === true) {
      frequencies.hands_touching_alert++;
    }
  });

  // Convert to frequencies per second
  const windowSeconds = gestureDataWindow.length * 0.5;
  Object.keys(frequencies).forEach((key) => {
    frequencies[key] = windowSeconds > 0 ? frequencies[key] / windowSeconds : 0;
  });

  return frequencies;
}

/**
 * Detects all alerts and warnings in gesture data
 * Returns an object with detected alerts and their details
 * @param {Object} gestureData - Gesture data object
 * @returns {Object} - { alerts: [], warnings: [], totalMagnitude: number }
 */
export function detectAlerts(gestureData) {
  if (!gestureData) {
    return { alerts: [], warnings: [], totalMagnitude: 0, hasAlert: false, level: 'normal' };
  }

  // Debug: log actual gestureData occasionally to avoid console flood
  if (Math.random() < 0.01) {
    console.log('detectAlerts - Gesture data:', gestureData);
  }

  // New ai-service format: all treated as alerts (no warning distinction)
  const isNewFormat = gestureData.classification !== undefined || (gestureData.alert && gestureData.duration_seconds !== undefined);
  if (isNewFormat) {
    const { region, category } = inferRegionFromNewFormat(gestureData);
    const classification = gestureData.classification || 'sustained';
    const magnitude = classification === 'gesture' ? 80 : 50;
    const label = [gestureData.alert, gestureData.brows].filter(Boolean).join(' - ') || 'Alert';
    const item = {
      type: gestureData.alert || gestureData.gesture_name || 'gesture',
      label,
      magnitude: magnitude / 100,
      category,
      region,
    };
    const alerts = [item];
    const totalMagnitude = magnitude / 100;
    return {
      alerts,
      totalMagnitude,
      hasAlert: true,
      level: 'alert',
    };
  }

  // Legacy backend format (brows_gesture_type, etc.)
  const hasBackendFiltering = gestureData.brows_gesture_type !== undefined ||
    gestureData.brows_should_show !== undefined;

  if (hasBackendFiltering) {
    const result = detectAlertsFromBackend(gestureData);
    const allAlerts = [...result.alerts, ...result.warnings];
    return {
      alerts: allAlerts,
      warnings: [],
      totalMagnitude: result.totalMagnitude,
      hasAlert: allAlerts.length > 0,
      level: allAlerts.length > 0 ? 'alert' : 'normal',
    };
  }

  // Legacy per-frame format: use old field names (nodding, shaking, etc.)
  const alerts = [];
  const warnings = [];
  let totalMagnitude = 0;

  // RED ALERTS - Facial expressions (face)
  // Brows
  if (
    (gestureData.brows === 'brows_down' || gestureData.brows === 'brows_up') &&
    gestureData.brow_alert === true &&
    true
  ) {
    alerts.push({
      type: 'brows',
      label: 'Brows',
      magnitude: ALERT_MAGNITUDES.brows_alert,
      category: 'facial_expression',
      region: 'brows',
    });
    totalMagnitude += ALERT_MAGNITUDES.brows_alert;
  }

  // Lips
  if (
    gestureData.is_speaking === false &&
    gestureData.lip_alert === true &&
    (gestureData.lips === 'lips_pressed' ||
      gestureData.lips === 'lips_inverted' ||
      gestureData.lips === 'lips_back') &&
    true
  ) {
    alerts.push({
      type: 'lips',
      label: 'Lips',
      magnitude: ALERT_MAGNITUDES.lips_alert,
      category: 'facial_expression',
      region: 'lips',
    });
    totalMagnitude += ALERT_MAGNITUDES.lips_alert;
  }

  // Nose
  if (
    gestureData.nose === 'nose_wrinkled' &&
    gestureData.nose_alert === true &&
    true
  ) {
    alerts.push({
      type: 'nose',
      label: 'Nose',
      magnitude: ALERT_MAGNITUDES.nose_alert,
      category: 'facial_expression',
      region: 'nose',
    });
    totalMagnitude += ALERT_MAGNITUDES.nose_alert;
  }

  // Eye status
  if (
    gestureData.eye_status === 'wide_open' &&
    true
  ) {
    alerts.push({
      type: 'eye_status',
      label: 'Eyes wide open',
      magnitude: ALERT_MAGNITUDES.eye_wide_open,
      category: 'facial_expression',
      region: 'eyes',
    });
    totalMagnitude += ALERT_MAGNITUDES.eye_wide_open;
  }

  // Mouth twisted (future field)
  if (gestureData.mouth_twisted) {
    if (
      gestureData.mouth_twisted === 'left' &&
      gestureData.mouth_alert === true &&
      true
    ) {
      alerts.push({
        type: 'mouth_twisted',
        label: 'Mouth twisted',
        magnitude: ALERT_MAGNITUDES.mouth_twisted_left,
        category: 'facial_expression',
        region: 'mouth',
      });
      totalMagnitude += ALERT_MAGNITUDES.mouth_twisted_left;
    } else if (
      gestureData.mouth_twisted === 'right' &&
      true
    ) {
      alerts.push({
        type: 'mouth_twisted',
        label: 'Mouth twisted',
        magnitude: ALERT_MAGNITUDES.mouth_twisted_right,
        category: 'facial_expression',
        region: 'mouth',
      });
      totalMagnitude += ALERT_MAGNITUDES.mouth_twisted_right;
    }
  }

  // RED ALERTS - Head gestures (full head)
  // Check for nodding - accept any truthy value (true, 1, "true", etc.)
  if (
    gestureData.nodding === true &&
    true
  ) {
    alerts.push({
      type: 'nodding',
      label: 'Nodding',
      magnitude: ALERT_MAGNITUDES.nodding,
      category: 'head_gesture',
      region: 'full_head',
    });
    totalMagnitude += ALERT_MAGNITUDES.nodding;
  }

  if (
    gestureData.shaking === true &&
    true
  ) {
    alerts.push({
      type: 'shaking',
      label: 'Shaking',
      magnitude: ALERT_MAGNITUDES.shaking,
      category: 'head_gesture',
      region: 'full_head',
    });
    totalMagnitude += ALERT_MAGNITUDES.shaking;
  }

  // ORANGE WARNINGS - Light head gestures
  if (
    gestureData.light_nodding === true &&
    true
  ) {
    warnings.push({
      type: 'light_nodding',
      label: 'Light nodding',
      magnitude: ALERT_MAGNITUDES.light_nodding,
      category: 'head_gesture',
    });
    totalMagnitude += ALERT_MAGNITUDES.light_nodding;
  }

  if (
    gestureData.light_shaking === true &&
    true
  ) {
    warnings.push({
      type: 'light_shaking',
      label: 'Light shaking',
      magnitude: ALERT_MAGNITUDES.light_shaking,
      category: 'head_gesture',
    });
    totalMagnitude += ALERT_MAGNITUDES.light_shaking;
  }

  if (
    gestureData.shrug_detected === true &&
    true
  ) {
    alerts.push({
      type: 'shrug',
      label: 'Shrug',
      magnitude: ALERT_MAGNITUDES.shrug_detected,
      category: 'head_gesture',
      region: 'full_head',
    });
    totalMagnitude += ALERT_MAGNITUDES.shrug_detected;
  }

  // RED ALERTS - Self-touching
  if (
    gestureData.index_below_nose_touched === true &&
    true
  ) {
    alerts.push({
      type: 'index_below_nose_touched',
      label: 'Index below nose touched',
      magnitude: ALERT_MAGNITUDES.index_below_nose_touched,
      category: 'self_touching',
      region: 'hands',
    });
    totalMagnitude += ALERT_MAGNITUDES.index_below_nose_touched;
  }

  // Contempt alert
  if (
    gestureData.contempt_alert === true &&
    true
  ) {
    alerts.push({
      type: 'contempt',
      label: 'Contempt',
      magnitude: ALERT_MAGNITUDES.contempt_alert,
      category: 'facial_expression',
      region: 'face',
    });
    totalMagnitude += ALERT_MAGNITUDES.contempt_alert;
  }

  // ORANGE WARNINGS - Head pose and direction
  if (
    (gestureData.head_pose === 'tilted right' || gestureData.head_pose === 'Tilted right') &&
    true
  ) {
    warnings.push({
      type: 'head_pose',
      label: 'Head tilted right',
      magnitude: ALERT_MAGNITUDES.head_pose_tilted_right,
      category: 'head_gesture',
    });
    totalMagnitude += ALERT_MAGNITUDES.head_pose_tilted_right;
  }

  if (gestureData.direction) {
    if (
      (gestureData.direction === 'up_right' ||
        gestureData.direction === 'right' ||
        gestureData.direction === 'left' ||
        gestureData.direction === 'up_left') &&
      true
    ) {
      warnings.push({
        type: 'direction',
        label: `Gaze ${gestureData.direction}`,
        magnitude: ALERT_MAGNITUDES.direction_right,
        category: 'head_gesture',
      });
      totalMagnitude += ALERT_MAGNITUDES.direction_right;
    }
  }

  // ORANGE WARNINGS - Self-touching
  if (
    gestureData.cranium_touched === true &&
    true
  ) {
    warnings.push({
      type: 'cranium_touched',
      label: 'Cranium touched',
      magnitude: ALERT_MAGNITUDES.cranium_touched,
      category: 'self_touching',
    });
    totalMagnitude += ALERT_MAGNITUDES.cranium_touched;
  }

  if (
    gestureData.eye_touched === true &&
    true
  ) {
    warnings.push({
      type: 'eye_touched',
      label: 'Eye touched',
      magnitude: ALERT_MAGNITUDES.eye_touched,
      category: 'self_touching',
    });
    totalMagnitude += ALERT_MAGNITUDES.eye_touched;
  }

  if (
    gestureData.ear_touched === true &&
    true
  ) {
    warnings.push({
      type: 'ear_touched',
      label: 'Ear touched',
      magnitude: ALERT_MAGNITUDES.ear_touched,
      category: 'self_touching',
    });
    totalMagnitude += ALERT_MAGNITUDES.ear_touched;
  }

  if (
    gestureData.nose_touched === true &&
    true
  ) {
    warnings.push({
      type: 'nose_touched',
      label: 'Nose touched',
      magnitude: ALERT_MAGNITUDES.nose_touched,
      category: 'self_touching',
    });
    totalMagnitude += ALERT_MAGNITUDES.nose_touched;
  }

  if (
    gestureData.pinching_nose_touched === true &&
    true
  ) {
    warnings.push({
      type: 'pinching_nose_touched',
      label: 'Pinching nose',
      magnitude: ALERT_MAGNITUDES.pinching_nose_touched,
      category: 'self_touching',
    });
    totalMagnitude += ALERT_MAGNITUDES.pinching_nose_touched;
  }

  // TEMPORARILY DISABLED: neck_touched - not detecting correctly
  // if (
  //   gestureData.neck_touched === true &&
  //   shouldShowGesture('neck_touched', gestureData, true)
  // ) {
  //   warnings.push({
  //     type: 'neck_touched',
  //     label: 'Neck touched',
  //     magnitude: ALERT_MAGNITUDES.neck_touched,
  //     category: 'self_touching',
  //   });
  //   totalMagnitude += ALERT_MAGNITUDES.neck_touched;
  // }

  // ORANGE WARNINGS - Hands
  if (
    gestureData.hands_velocity_alert === true &&
    true
  ) {
    warnings.push({
      type: 'hands_velocity',
      label: 'Hands velocity',
      magnitude: ALERT_MAGNITUDES.hands_velocity_alert,
      category: 'hands',
    });
    totalMagnitude += ALERT_MAGNITUDES.hands_velocity_alert;
  }

  if (
    gestureData.hands_touching_alert === true &&
    true
  ) {
    warnings.push({
      type: 'hands_touching',
      label: 'Hands touching',
      magnitude: ALERT_MAGNITUDES.hands_touching_alert,
      category: 'hands',
    });
    totalMagnitude += ALERT_MAGNITUDES.hands_touching_alert;
  }

  const allAlerts = [...alerts, ...warnings];
  return {
    alerts: allAlerts,
    warnings: [],
    totalMagnitude: Math.min(100, totalMagnitude * 100),
    hasAlert: allAlerts.length > 0,
    level: allAlerts.length > 0 ? 'alert' : 'normal',
  };
}

/**
 * Detects which specific face regions have alerts (for landmark coloring)
 * @param {Object} gestureData - Gesture data object
 * @param {Array} alerts - Pre-calculated alerts array (optional)
 * @returns {Object} - { brows: 'error'|'success', lips: ..., nose: ..., eyes: ..., mouth: ... }
 */
export function detectFaceAlertRegions(gestureData, alerts = null) {
  const regions = {
    brows: 'success',
    lips: 'success',
    nose: 'success',
    eyes: 'success',
    mouth: 'success',
  };

  if (!gestureData) return regions;

  if (alerts === null) {
    const result = detectAlerts(gestureData);
    alerts = result.alerts;
  }

  alerts.forEach((alert) => {
    if (alert.category === 'facial_expression' && alert.region) {
      if (alert.region === 'brows') regions.brows = 'error';
      if (alert.region === 'lips') {
        regions.lips = 'error';
        regions.mouth = 'error';
      }
      if (alert.region === 'nose') regions.nose = 'error';
      if (alert.region === 'eyes') regions.eyes = 'error';
      if (alert.region === 'mouth') {
        regions.mouth = 'error';
        regions.lips = 'error';
      }
    }
  });

  return regions;
}

/**
 * Detects if there are head gestures that color the entire head
 * @param {Object} gestureData - Gesture data object
 * @param {Array} alerts - Pre-calculated alerts array (optional)
 * @returns {Object} - { fullHead: 'error'|'success', hasHeadGesture: boolean }
 */
export function detectHeadGestures(gestureData, alerts = null) {
  if (!gestureData) {
    return { fullHead: 'success', hasHeadGesture: false };
  }

  if (alerts === null) {
    const result = detectAlerts(gestureData);
    alerts = result.alerts;
  }

  const hasHeadGestureAlert = alerts.some(
    (alert) => alert.category === 'head_gesture' && alert.region === 'full_head'
  );

  if (hasHeadGestureAlert) {
    return { fullHead: 'error', hasHeadGesture: true };
  }

  return { fullHead: 'success', hasHeadGesture: false };
}

/**
 * Gets the overall alert level and color for landmarks
 * @param {Object} gestureData - Gesture data object
 * @returns {Object} - { level: 'alert'|'normal', faceRegions: {...}, fullHead: 'error'|'success', hands: 'error'|'success' }
 */
export function getLandmarkColors(gestureData) {
  const { alerts, level } = detectAlerts(gestureData);
  const faceRegions = detectFaceAlertRegions(gestureData, alerts);
  const { fullHead } = detectHeadGestures(gestureData, alerts);

  const hasHandsAlert = alerts.some((a) => a.category === 'hands' || a.region === 'hands');
  const handsColor = hasHandsAlert ? 'error' : 'success';

  return {
    level,
    faceRegions,
    fullHead,
    hands: handsColor,
  };
}

/**
 * Calculates the alert level (alert/warning/normal) based on gesture data
 * @param {Object} gestureData - Gesture data object
 * @returns {string} - 'alert' | 'normal'
 */
export function calculateAlertLevel(gestureData) {
  const { level } = detectAlerts(gestureData);
  return level;
}

/**
 * Calculates the total magnitude sum of all detected alerts
 * @param {Object} gestureData - Gesture data object
 * @returns {number} - Total magnitude as percentage (0-100)
 */
export function calculateMagnitudeSum(gestureData) {
  const { totalMagnitude } = detectAlerts(gestureData);
  return totalMagnitude;
}

export default {
  detectAlerts,
  detectFaceAlertRegions,
  detectHeadGestures,
  getLandmarkColors,
  calculateAlertLevel,
  calculateMagnitudeSum,
  normalizeAnalysisMethodFields,
  methodChipLabel,
  discardReasonLabel,
  buildGestureMethodIndex,
  lookupGestureMethodInfo,
};

